import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Sparkles } from 'lucide-react';
import Chatbot from './Chatbot';

interface ChatBubbleProps {
  showAfterScroll?: number; // px to scroll before showing
  position?: 'bottom-right' | 'bottom-left';
  theme?: 'light' | 'dark' | 'auto';
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  showAfterScroll = 300,
  position = 'bottom-right',
  theme = 'auto'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [shouldCompact, setShouldCompact] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const isMobile = window.innerWidth < 768;

      setIsVisible(scrollPosition > showAfterScroll);

      // Compact mode when scrolling fast or near bottom on mobile
      const nearBottom = scrollPosition + windowHeight > documentHeight - 300;
      setShouldCompact(isMobile && (nearBottom || scrollPosition > 1000));
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showAfterScroll]);

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4 md:bottom-6 md:right-6',
    'bottom-left': 'bottom-4 left-4 md:bottom-6 md:left-6'
  };

  return (
    <>
      {/* Chatbot — always mounted so conversation state persists */}
      <Chatbot
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        position={position}
        theme={theme}
      />

      {/* Bubble button — hidden while chat is open */}
      {!chatOpen && isVisible && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20
          }}
          className={`fixed ${positionClasses[position]} z-50`}
        >
          {/* Pulse animation rings - hide when compact */}
          {!shouldCompact && (
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full"
            />
          )}

          {/* Main button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            onClick={() => setChatOpen(true)}
            className={`
              relative ${shouldCompact ? 'w-10 h-10' : 'w-12 h-12 md:w-14 md:h-14'}
              bg-gradient-to-br from-purple-500 to-blue-500
              text-white rounded-full shadow-lg hover:shadow-xl
              flex items-center justify-center
              transition-all duration-300
              focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
              group overflow-hidden
            `}
            aria-label="Chat with Thanh's AI Assistant"
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Icon */}
            <motion.div
              animate={isHovered ? { rotate: 360 } : {}}
              transition={{ duration: 0.5 }}
              className="relative z-10"
            >
              <MessageCircle size={shouldCompact ? 16 : 20} className="md:w-6 md:h-6" />
            </motion.div>

            {/* Sparkle effects - hide when compact */}
            {!shouldCompact && (
              <motion.div
                animate={{
                  scale: [0, 1, 0],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: 0.5
                }}
                className="absolute top-1 right-1 text-yellow-300"
              >
                <Sparkles size={8} />
              </motion.div>
            )}
          </motion.button>

          {/* Tooltip - hide when compact or mobile */}
          {!shouldCompact && (
            <motion.div
              initial={{ opacity: 0, x: 10, scale: 0.8 }}
              animate={{
                opacity: isHovered ? 1 : 0,
                x: isHovered ? 0 : 10,
                scale: isHovered ? 1 : 0.8
              }}
              transition={{ duration: 0.2 }}
              className={`
                absolute top-1/2 -translate-y-1/2
                ${position === 'bottom-right' ? 'right-full mr-3' : 'left-full ml-3'}
                px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg shadow-lg
                whitespace-nowrap pointer-events-none hidden md:block
              `}
            >
              <div className="flex items-center gap-2">
                <span>Ask about Thanh's background!</span>
              </div>

              {/* Arrow */}
              <div
                className={`
                  absolute top-1/2 -translate-y-1/2 w-0 h-0
                  ${position === 'bottom-right'
                    ? 'left-full border-l-4 border-l-gray-900 dark:border-l-gray-700'
                    : 'right-full border-r-4 border-r-gray-900 dark:border-r-gray-700'
                  }
                  border-y-2 border-y-transparent
                `}
              />
            </motion.div>
          )}

          {/* Floating particles - hide when compact */}
          {!shouldCompact && [...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [-20, -40, -20],
                x: [0, 5 * (i - 1), 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 2 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut"
              }}
              className="absolute w-1 h-1 bg-purple-300 rounded-full"
              style={{
                left: `${30 + i * 5}%`,
                bottom: '100%',
              }}
            />
          ))}
        </motion.div>
      )}
    </>
  );
};

export default ChatBubble;
